import React, { useState, useRef, useEffect } from 'react';
import { getAuth, signOut } from "firebase/auth";
import { useUserContext } from "src/components/ProtectedRoute/userProvider";
import { getAccountBalance } from "src/apiCalls";
import { useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';
import { styled, alpha, lighten } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import { SignInModal } from 'src/components/Auth/SignInModal';
import logger from 'src/logger';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${alpha(theme.palette.primary.main, 0.08)};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)};
`
);

function HeaderUserbox() {
  const { user, setUser } = useUserContext();
  const auth = getAuth();
  const [name, setName] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [totalSales, setTotalSales] = useState(0);
  const [showSignInModal, setShowSignInModal] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.displayName);
      setPhotoUrl(user.photoURL);
      getTotalSales();
    } else {
      setName(null);
      setPhotoUrl(null);
      setTotalSales(0);
    }
  }, [user]);

  const getTotalSales = async () => {
    try {
      const data = await getAccountBalance(user.accessToken, user.uid);
      if(data){
        setTotalSales(data);
      }
    } catch (error) {
      logger.error("Failed to fetch user details:", error);
    }
  };

  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        setName(null);
        setPhotoUrl(null);
        setTotalSales(0);
        setUser(null); // Explicitly set user to null
        handleClose(); // Close the popover after sign-out
        logger.info('User signed out successfully');
      })
      .catch((error) => {
        logger.error('Error signing out: ', error);
      });
  };

  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {user ? (
        <>
          <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
            <Avatar variant="rounded" alt={name} src={photoUrl} />
            <Hidden mdDown>
              <UserBoxText>
                <UserBoxLabel variant="body1">{name}</UserBoxLabel>
                <UserBoxDescription variant="body2">
                  Total Sales: £{totalSales}
                </UserBoxDescription>
              </UserBoxText>
            </Hidden>
            <Hidden smDown>
              <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
            </Hidden>
          </UserBoxButton>
          <Popover
            anchorEl={ref.current}
            onClose={handleClose}
            open={isOpen}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <MenuUserBox sx={{ minWidth: 210 }} display="flex">
              <Avatar variant="rounded" alt={name} src={photoUrl} />
              <UserBoxText>
                <UserBoxLabel variant="body1">{name}</UserBoxLabel>
                <UserBoxDescription variant="body2">
                Total Sales: £{totalSales}
                </UserBoxDescription>
              </UserBoxText>
            </MenuUserBox>
            <Divider sx={{ mb: 0 }} />
            <List sx={{ p: 1 }} component="nav">
              <ListItem button to="/accounts/profile" component={NavLink}>
                <AccountBoxTwoToneIcon fontSize="small" />
                <ListItemText primary="My Profile" />
              </ListItem>
            </List>
            <Divider />
            <Box sx={{ m: 1 }}>
              <Button color="primary" onClick={handleSignOut} fullWidth>
                <LockOpenTwoToneIcon sx={{ mr: 1 }} />
                Sign out
              </Button>
            </Box>
          </Popover>
        </>
      ) : (
        <Button color="primary" onClick={() => setShowSignInModal(true)}>
          <LockOpenTwoToneIcon sx={{ mr: 1 }} />
          Sign in
        </Button>
      )}

      <SignInModal 
        open={showSignInModal} 
        onClose={() => setShowSignInModal(false)} 
      />
    </>
  );
}

export default HeaderUserbox;
